import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { JobPosting } from '../types';
import { apiService } from '../services/api';
import { markJobViewed } from '../utils/viewedStore';
import { Loading } from '../components';
import { useDataDictionary, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictDisplayValue, getDictValueByCode, resolveApiError } from '../utils/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Star, MapPin, DollarSign, Briefcase, User, Calendar, MessageCircle, Heart, Home, Clock, AlertCircle, ArrowLeft, Share2, CheckCircle, Send, Users, Baby, Milk, Smile, GraduationCap, Accessibility, Dog, BedDouble, Bookmark } from 'lucide-react';
import { formatDate } from '../utils/date';

const JobPostingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { toggleJobPostBookmark, isJobPostBookmarked } = useBookmarks();
  const { t } = useTranslation();
  const [jobPosting, setJobPosting] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState('');
  const [recommended, setRecommended] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [showAllRec, setShowAllRec] = useState(false);
  const [showAllApp, setShowAllApp] = useState(false);
  const qualifiedHelpers = useMemo(() => recommended.filter((m) => m.matchPercent >= 80), [recommended]);

  const { dictionaries: careDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE });
  const { dictionaries: houseworkDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.HOUSEWORK });
  const { dictionaries: cookingDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.COOKING_SKILLS });
  const { dictionaries: otherSkillsDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.OTHER_SKILLS });
  const { dictionaries: educationDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.EDUCATION_LEVEL });
  const { dictionaries: nationalityDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.NATIONALITY });
  const { dictionaries: religionDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.RELIGION });
  const { dictionaries: locationDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.JOB_LOCATION });
  const { dictionaries: livingArrangementDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.LIVING_ARRANGEMENT });
  const { dictionaries: languageDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS });
  const { dictionaries: benefitsDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.BENEFITS });

  useEffect(() => {
    if (!id) return;
    markJobViewed(parseInt(id));
    const isEmployer = user?.role === 'EMPLOYER';
    const isHelper = user?.role === 'HELPER';
    const load = async () => {
      try {
        setLoading(true);
        const [postRes, recRes, appRes, helperAppRes] = await Promise.all([
          apiService.jobPostings.getById(parseInt(id)),
          isEmployer ? apiService.jobPostings.getRecommendedHelpers(parseInt(id), 20) : Promise.resolve(null),
          isEmployer ? apiService.jobApplications.getByJobPosting(parseInt(id)) : Promise.resolve(null),
          isHelper && user?.id ? apiService.jobApplications.getByHelper(user.id) : Promise.resolve(null),
        ]);
        if (postRes.success && postRes.data) {
          setJobPosting(postRes.data);
        } else {
          setError(t('jobDetail.notFound', '找不到職位資料'));
        }
        if (recRes && recRes.success && recRes.data) setRecommended(recRes.data);
        if (appRes) {
          if (appRes.success && appRes.data) setApplications(appRes.data);
        }
        if (helperAppRes?.success && helperAppRes.data) {
          const alreadyApplied = helperAppRes.data.some((a: any) => a.jobPostingId === parseInt(id));
          if (alreadyApplied) setApplied(true);
        }
      } catch (error) {
        console.error('Error loading job posting:', error);
        setError(t('jobDetail.loadFailed', '載入職位資料失敗'));
      } finally {
        setLoading(false);
      }
    };
    void load();
    void recordView();
  }, [id, user?.id]);

  const recordView = async () => {
    if (!id) return;
    try {
      const viewerId = user?.id || undefined;
      const viewerType = user?.role || 'ANONYMOUS';
      await apiService.statistics.recordJobPostingView(parseInt(id), viewerId, viewerType);
    } catch (error) {
      console.error('Error recording view:', error);
    }
  };


  const handleApplyInterest = async () => {
    if (!user) {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      navigate('/login-form');
      return;
    }
    if (user.role !== 'HELPER' || !jobPosting) return;
    try {
      setContacting(true);
      const response = await apiService.jobApplications.apply(jobPosting.id, {
        helperId: user.id,
        message: '',
      });
      if (response.success) {
        setApplied(true);
        showAlert({ type: 'success', message: t('jobDetail.interestSent', '已表達興趣，僱主將收到通知！') });
      } else {
        showAlert({ type: 'error', message: resolveApiError(response.errorCode, response.message, t) });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('jobForm.opFailedRetry', '操作失敗，請稍後再試');
      showAlert({ type: 'error', message: msg });
    } finally {
      setContacting(false);
    }
  };

  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      navigate('/login-form');
      return;
    }
    if (!jobPosting) return;
    try {
      await toggleJobPostBookmark(jobPosting);
      if (user?.role === 'HELPER' && user?.id) {
        try {
          await apiService.statistics.recordJobPostBookmark(user.id, jobPosting.id);
        } catch (err) {
          console.error('Error recording job post bookmark:', err);
        }
      }
    } catch (err) {
      showAlert({ type: 'error', message: t('jobDetail.bookmarkFailed', '收藏操作失敗，請稍後再試') });
    }
  };

  const formatSalary = (posting: JobPosting) => {
    if (!user && (posting.salaryType === 'FIXED' || posting.salaryType === 'RANGE') &&
        !posting.fixedSalary && !posting.minSalary && !posting.maxSalary) {
      return `🔒 ${t('jobDetail.loginToViewBudget', '登入查看預算')}`;
    }
    if (posting.salaryType === 'FIXED' && posting.fixedSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.fixedSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'RANGE' && posting.minSalary && posting.maxSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.minSalary.toLocaleString()} - ${currency}${posting.maxSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'OTHER') {
      if (posting.salaryOtherOption === 'BASED_ON_EXPERIENCE') return t('jobPostings.my.salary.basedOnExperience', '根據經驗');
      if (posting.salaryOtherOption === 'LEGAL_MINIMUM') return t('jobForm.salaryLegalMin', '法定最低工資');
      if (posting.salaryOtherOption === 'TO_BE_DISCUSS') return t('jobForm.salaryNegotiable', '面議');
    }
    return t('jobPostings.my.salary.notSpecified', '不指定');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return t('common.active');
      case 'CLOSED': return t('common.closed');
      case 'DRAFT': return t('jobPosting.status.draft', '草稿');
      default: return status;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'default';
      case 'CLOSED': return 'destructive';
      case 'DRAFT': return 'secondary';
      default: return 'outline';
    }
  };

  const getSkillDisplayValue = (skillCode: string, skillType: 'care' | 'housework' | 'cooking' | 'other'): string => {
    let dict;
    if (skillType === 'care') dict = careDicts.find(d => d.code === skillCode);
    else if (skillType === 'housework') dict = houseworkDicts.find(d => d.code === skillCode);
    else if (skillType === 'cooking') dict = cookingDicts.find(d => d.code === skillCode);
    else dict = otherSkillsDicts.find(d => d.code === skillCode);
    return dict ? getDictDisplayValue(dict) : skillCode;
  };

  const getSkillType = (skillCode: string): 'care' | 'housework' | 'cooking' | 'other' => {
    if (careDicts.some(d => d.code === skillCode)) return 'care';
    if (houseworkDicts.some(d => d.code === skillCode)) return 'housework';
    if (cookingDicts.some(d => d.code === skillCode)) return 'cooking';
    return 'other';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12 flex items-center justify-center">
        <Loading fullScreen={false} message={t('common.loading', '載入中...')} />
      </div>
    );
  }

  if (error || !jobPosting) {
    return (
      <div className="min-h-screen bg-background py-12 px-4 flex flex-col items-center justify-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-foreground mb-6">{error || t('jobDetail.notFound', '找不到職位資料')}</h2>
        <Button onClick={() => navigate(user?.role === 'HELPER' ? '/home' : '/job-postings')}>
          ← {t('common.back')}
        </Button>
      </div>
    );
  }

  const isOwner = user?.role === 'EMPLOYER' && jobPosting.employerId === user.id;
  const showBookmarkForHelper = (!user || user.role === 'HELPER') && jobPosting.status === 'ACTIVE';


  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header Actions */}
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="ghost"
              className="gap-2"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.back', '返回')}
            </Button>
            {isOwner && (
              <Button onClick={() => navigate(`/job-postings/${id}/edit`)}>
                {t('jobDetail.editJob', '編輯職位')}
              </Button>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Header Info */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex gap-2 mb-2">
                      <Badge variant={getStatusVariant(jobPosting.status) as any}>
                        {getStatusLabel(jobPosting.status)}
                      </Badge>
                      {jobPosting.workType && (
                        <Badge variant="secondary">
                          {jobPosting.workType === 'FULL_TIME' ? t('jobPostings.my.workType.fullTime', '全職') : jobPosting.workType === 'PART_TIME' ? t('jobPostings.my.workType.partTime', '兼職') : t('jobPostings.my.workType.temporary', '臨時')}
                        </Badge>
                      )}
                    </div>
                    <h1 className="text-3xl font-bold text-foreground">{jobPosting.headline || jobPosting.title}</h1>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-muted-foreground text-sm">
                      {jobPosting.employerName && (
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {jobPosting.employerName.split(' ').pop()}
                        </span>
                      )}
                      {jobPosting.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {getDictValueByCode(locationDicts, jobPosting.location)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(jobPosting.postedDate)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      showAlert({ type: 'success', message: t('helperDetail.linkCopied', '已複製連結') });
                    }}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* 家庭成員 + 職位描述 */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('common.familyMembers')}</CardTitle>
                  {jobPosting.employerNationality && (
                    <p className="text-sm text-muted-foreground">
                      {t('jobForm.employerNationality', '僱主國籍')}：<span className="font-medium text-foreground">{getDictValueByCode(nationalityDicts, jobPosting.employerNationality)}</span>
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {jobPosting.familyMembers && (
                    <div className="flex flex-wrap gap-4">
                      {[
                        { label: t('helperProfileForm.familyAdults', '成人'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.adults, icon: <Users className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyNewborn', '初生 (0-6個月)'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.newborn, icon: <Baby className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyInfant', '嬰兒 (6個月-3歲)'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.infant ?? jobPosting.familyMembers.children0to3, icon: <Milk className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyChild', '小童 (3-12歲)'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.children ?? jobPosting.familyMembers.children3to12, icon: <Smile className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyTeen', '少年 (12-18歲)'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.teen ?? jobPosting.familyMembers.children12to18, icon: <GraduationCap className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyElderly', '長者 (65歲+)'), unit: t('jobDetail.unitPerson', '人'), value: jobPosting.familyMembers.elderly, icon: <Accessibility className="h-5 w-5 text-muted-foreground" /> },
                        { label: t('helperProfileForm.familyPets', '寵物'), unit: t('jobDetail.unitAnimal', '隻'), value: jobPosting.familyMembers.pets, icon: <Dog className="h-5 w-5 text-muted-foreground" /> },
                      ].filter(item => item.value).map(item => (
                        <div key={item.label} className="flex items-center gap-2">
                          {item.icon}
                          <div>
                            <p className="text-xs text-muted-foreground">{item.label}</p>
                            <p className="font-medium text-sm">{item.value} {item.unit}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {jobPosting.description && (
                    <>
                      <Separator />
                      <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap break-words">
                        {jobPosting.description}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* 要求 */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('jobDetail.requirements', '要求')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 text-sm">
                    {jobPosting.requiredEducationLevel && (
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">{t('jobForm.educationLabel', '教育程度')}</span>
                        <span className="font-medium text-foreground">{getDictValueByCode(educationDicts, jobPosting.requiredEducationLevel)}</span>
                      </div>
                    )}
                    {(jobPosting.minExperienceYears !== undefined && jobPosting.maxExperienceYears !== undefined) && (
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">{t('jobForm.workExpRange', '工作經驗')}</span>
                        <span className="font-medium text-foreground">{jobPosting.minExperienceYears} - {jobPosting.maxExperienceYears} {t('helperDetail.yrs', '年')}</span>
                      </div>
                    )}
                    {jobPosting.preferredGender && jobPosting.preferredGender !== 'ANY' && (
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">{t('jobDetail.genderPref', '性別偏好')}</span>
                        <span className="font-medium text-foreground">{jobPosting.preferredGender === 'MALE' ? t('common.male') : t('common.female')}</span>
                      </div>
                    )}
                    {jobPosting.preferredNationality && jobPosting.preferredNationality !== 'ANY' && (
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">{t('jobDetail.nationalityPref', '國籍偏好')}</span>
                        <span className="font-medium text-foreground">{getDictValueByCode(nationalityDicts, jobPosting.preferredNationality)}</span>
                      </div>
                    )}
                    {jobPosting.preferredReligion && jobPosting.preferredReligion !== 'ANY' && (
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">{t('jobDetail.religionPref', '宗教偏好')}</span>
                        <span className="font-medium text-foreground">{getDictValueByCode(religionDicts, jobPosting.preferredReligion)}</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium text-foreground mb-3">{t('jobForm.languageReq', '語言')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(new Set([
                        ...(jobPosting.requiredLanguages ? jobPosting.requiredLanguages.split(',').map(s => s.trim()).filter(Boolean) : []),
                        ...(jobPosting.spokenLanguages ?? []),
                      ])).map((lang, idx) => (
                        <Badge key={idx} variant="secondary">{getDictValueByCode(languageDicts, lang) || lang}</Badge>
                      ))}
                    </div>
                  </div>

                  {jobPosting.requiredSkills && jobPosting.requiredSkills.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-medium text-foreground mb-3">{t('jobDetail.professionalSkills', '技能要求')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {jobPosting.requiredSkills.map((skill, index) => {
                            const skillType = getSkillType(skill);
                            const displayValue = getSkillDisplayValue(skill, skillType);
                            return <Badge key={index} variant="outline">{displayValue}</Badge>;
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {jobPosting.cookingSkills && jobPosting.cookingSkills.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-medium text-foreground mb-3">{t('jobDetail.cookingSkillsRequired', '烹飪技能要求')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {jobPosting.cookingSkills.map((skill, index) => {
                            const displayValue = getSkillDisplayValue(skill, 'cooking');
                            return <Badge key={index} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{displayValue}</Badge>;
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {jobPosting.otherSkills && jobPosting.otherSkills.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-medium text-foreground mb-3">{t('jobPosting.fields.additionalSkills', 'Additional Skills')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {jobPosting.otherSkills.map((skill, index) => {
                            const displayValue = getSkillDisplayValue(skill, 'other');
                            return <Badge key={index} variant="outline">{displayValue}</Badge>;
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* 特殊需要 */}
              {jobPosting.specialRequirements && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('jobDetail.specialNeeds', '特殊需要')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground bg-muted p-4 rounded-md border">
                      {jobPosting.specialRequirements}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Apply Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">{t('jobForm.salaryLabel', '薪金')}</p>
                      <p className="text-2xl font-bold text-primary">
                        {formatSalary(jobPosting)}
                      </p>
                    </div>
                    <Separator />
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {t('common.restDay')}
</span>
                        <span className="font-medium">
                          {jobPosting.restDayOption === 'ANY' ? t('jobForm.any', '不限') :
                           jobPosting.restDayOption === 'WEEKDAY' ? t('jobDetail.weekday', '平日') :
                           jobPosting.restDayOption === 'SATURDAY' ? t('jobDetail.saturday', '星期六') :
                           jobPosting.restDayOption === 'SUNDAY' ? t('jobDetail.sunday', '星期日') :
                           jobPosting.restDayOption === 'TO_BE_DISCUSS' ? t('jobForm.salaryNegotiable', '面議') :
                           jobPosting.restDayOption === 'FLEXIBLE' ? t('jobDetail.flexible', '彈性') :
                           jobPosting.restDayOption || t('jobDetail.notStated', '未註明')}
                        </span>
                      </div>
                      {jobPosting.livingArrangement && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <BedDouble className="h-4 w-4" />
                            {t('jobForm.livingArrangement', '住宿安排')}
                          </span>
                          <span className="font-medium">{getDictValueByCode(livingArrangementDicts, jobPosting.livingArrangement)}</span>
                        </div>
                      )}
                      {jobPosting.numberOfRooms && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Home className="h-4 w-4" />
                            {t('jobDetail.numberOfRooms', '房間數目')}
                          </span>
                          <span className="font-medium">{jobPosting.numberOfRooms} {t('jobDetail.rooms', '間')}</span>
                        </div>
                      )}
                      {jobPosting.flatSize && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Home className="h-4 w-4" />
                            {t('jobDetail.flatSize', '房屋面積')}
                          </span>
                          <span className="font-medium">{jobPosting.flatSize} {t('jobForm.sqftUnit', '呎')}</span>
                        </div>
                      )}
                      {jobPosting.requiredStartDate && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {t('jobForm.expectedStartDate', '期望上班日期')}
                          </span>
                          <span className="font-medium">
                            {jobPosting.requiredStartDate.replace(/-/g, '/')}
                          </span>
                        </div>
                      )}
                    </div>
                    <Separator />
                    
                    {jobPosting.status === 'ACTIVE' && user?.role !== 'EMPLOYER' && (
                      applied ? (
                        <Button className="w-full gap-2" disabled variant="secondary">
                          <CheckCircle className="h-4 w-4" />
                          {t('jobDetail.alreadyInterested', '已表達興趣')}
                        </Button>
                      ) : (
                        <Button
                          className="w-full gap-2"
                          onClick={handleApplyInterest}
                          disabled={contacting}
                        >
                          <Heart className="h-4 w-4" />
                          {contacting ? t('common.processing', '處理中...') : t('jobDetail.iAmInterested', '我有興趣')}
                        </Button>
                      )
                    )}

                    {showBookmarkForHelper && (
                      <Button
                        variant={isJobPostBookmarked(jobPosting.id) ? "secondary" : "outline"}
                        className="w-full gap-2"
                        onClick={handleBookmark}
                      >
                        <Bookmark
                          className={`h-4 w-4 ${isJobPostBookmarked(jobPosting.id) ? "fill-current" : ""}`}
                        />
                        {isJobPostBookmarked(jobPosting.id) ? t('common.bookmarked') : t('jobDetail.bookmark', '收藏職位')}
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Benefits */}
                {jobPosting.benefits && jobPosting.benefits.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('common.benefits')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {jobPosting.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            {getDictValueByCode(benefitsDicts, benefit) || benefit}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* 推薦外傭 */}
                {isOwner && (
                  <Card>
                    <CardHeader className="flex flex-row items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-500 shrink-0" />
                      <CardTitle className="text-lg flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
                        {t('jobDetail.recommendedHelpers', '推薦外傭')}（{qualifiedHelpers.length}）
                      </CardTitle>
                      <Button variant="ghost" size="sm" className="shrink-0" onClick={() => navigate(`/job-postings/${id}/recommendations`)}>
                        {t('common.viewAll', '查看全部')}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {qualifiedHelpers.length === 0
                        ? <p className="text-sm text-muted-foreground">{t('jobDetail.noRecommendedHelpers', '暫無符合條件的外傭')}</p>
                        : (
                          <div className="space-y-3">
                            {(showAllRec ? qualifiedHelpers : qualifiedHelpers.slice(0, 5)).map((m) => (
                              <div
                                key={m.helper.id}
                                className="flex items-center justify-between rounded-lg border p-3 cursor-pointer hover:bg-accent"
                                onClick={() => navigate(`/helper/${m.helper.id}`)}
                              >
                                <p className="min-w-0 flex-1 truncate font-medium">{m.helper.fullName}</p>
                                <Badge variant={m.matchPercent >= 90 ? 'default' : 'secondary'} className="shrink-0 ml-2">
                                  {m.matchPercent}%
                                </Badge>
                              </div>
                            ))}
                            {qualifiedHelpers.length > 5 && !showAllRec && (
                              <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAllRec(true)}>
                                {t('common.viewMore', '查看更多')}（{qualifiedHelpers.length - 5}）
                              </Button>
                            )}
                          </div>
                        )
                      }
                    </CardContent>
                  </Card>
                )}

                {/* 已收到的申請 */}
                {isOwner && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        {t('jobDetail.receivedApplications', '已收到的申請')}（{applications.length}）
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {applications.length === 0
                        ? <p className="text-sm text-muted-foreground">{t('jobDetail.noApplications', '暫未收到申請')}</p>
                        : (
                          <div className="space-y-3">
                            {(showAllApp ? applications : applications.slice(0, 5)).map((app: any) => (
                              <div
                                key={app.id}
                                className="flex items-center justify-between rounded-lg border p-3 cursor-pointer hover:bg-accent"
                                onClick={() => app.helperProfileId && navigate(`/helper/${app.helperProfileId}`)}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Avatar className="h-8 w-8 shrink-0">
                                    <AvatarImage src={app.helperAvatarUrl} alt={app.helperName} />
                                    <AvatarFallback>{app.helperName?.[0] ?? '?'}</AvatarFallback>
                                  </Avatar>
                                  <p className="truncate font-medium">{app.helperName || '—'}</p>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={
                                    app.status === 'ACCEPTED' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
                                    : app.status === 'REJECTED' ? 'border-red-500/20 bg-red-500/10 text-red-700'
                                    : 'border-amber-500/30 bg-amber-500/10 text-amber-700'
                                  }
                                >
                                  {app.status === 'ACCEPTED' ? t('jobDetail.statusAccepted', '已接受') : app.status === 'REJECTED' ? t('jobDetail.statusRejected', '已拒絕') : t('jobDetail.statusPending', '待處理')}
                                </Badge>
                              </div>
                            ))}
                            {applications.length > 5 && !showAllApp && (
                              <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAllApp(true)}>
                                {t('common.viewMore', '查看更多')}（{applications.length - 5}）
                              </Button>
                            )}
                          </div>
                        )
                      }
                    </CardContent>
                  </Card>
                )}

              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobPostingDetailPage;
